import { UprightEnvironment } from '@upright/common/environment';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';

/**
 * See https://docs.appcues.com/dev-api-data/javascript-api-developer
 * for the actual docs, and implement the amount of typing here that
 * is helpful for development.
 */
export type Appcues = {
  identify: (userId: string, properties?: AppcuesUserProperties) => void;
  group: (groupId: string, properties?: AppcuesGroupProperties) => void;
  page: () => void;
  track<T extends AppcuesEventId>(eventId: T, properties?: AppcuesEventPropertiesById[T]): void;
  show: (id: string) => void;
  //   clearShow: () => void;
  //   on: () => void;
  //   off: () => void;
  //   once: () => void;
  reset: () => void;
  debug: () => void;
};

/**
 * Only a flat object is supported, i.e. no nested objects or arrays.
 * Prepend with "plt" for easy grouping of these on Appcues.
 * Appcues will convert the keys from camelCaseWord to "Camel Case Word".
 *
 * If you change the keys here, the new ones will get pushed to Appcues for users
 * once the code is live. The old ones will not get deleted from Appcues but will linger there
 * until eternity.
 */
export type AppcuesUserProperties = {
  email: string;

  // A duplicate with "email", but is included so that we can populate "email" also from somewhere else
  // (such as HubSpot) while still showing the email the user has on the platform.
  pltEmail: string;
  pltNameFirst: string | undefined;
  pltNameLast: string | undefined;
  pltUsername: string | undefined;

  // The organization values below are duplicates, but are included so that we can show the
  // organization name also under the properties of a single user.
  pltOrganizationId: string | undefined;
  pltOrganizationName: string | undefined;
  pltOrganizationType: string | undefined;

  pltIsTeamUser: boolean;
  pltIsTeamTestUser: boolean;
  pltIsDmaFreemiumUser: boolean;
  pltEnvironment: UprightEnvironment;
};

/**
 * Only a flat object is supported, i.e. no nested objects or arrays.
 * Prepend with "plt" for easy grouping of these on Appcues.
 * Appcues will convert the keys from camelCaseWord to "Camel Case Word".
 */
export type AppcuesGroupProperties = {
  name: string;

  // The organization values below are duplicates, but are included to avoid confusion
  // of what the ID actually refers to for non-technical users.
  pltOrganizationId: string | undefined;
  pltOrganizationName: string | undefined;
  pltOrganizationType: string | undefined;
};

export type AppcuesSettings = unknown;

/**
 * These will show under "Application events" on Appcues.
 **/
export enum AppcuesEventId {
  // Checklist events for DMA Freemium (as of 9/2025)
  hasWatchedDmaFreemiumWelcomeVideo = 'hasWatchedDmaFreemiumWelcomeVideo',
  pageViewPlgMaterialityResults = 'pageViewPlgDmaMaterialityList',
  pageViewPlgDmaImpactMaterialitySmp = 'pageViewPlgDmaImpactMaterialitySmp',
  pageViewPlgDmaFinancialMaterialitySmp = 'pageViewPlgDmaFinancialMaterialitySmp',
  pageViewPlgDmaImpactSidebar = 'pageViewPlgDmaImpactSidebar',
  pageViewPlgDmaFinancialSidebar = 'pageViewPlgDmaFinancialSidebar',
  // Iterate your products & services
  productListPageViewed = 'productListPageViewed',
  productIndividualProductSettingDropDownOpened = 'productIndividualProductSettingDropDownOpened',
  productEditTitleFormInteraction = 'productEditTitleFormInteraction',
  productEditDescriptionFormInteraction = 'productEditDescriptionFormInteraction',
  productEditRevenueShareFormInteraction = 'productEditRevenueShareFormInteraction',
  productEditProductSaveClicked = 'productEditProductSaveClicked',
  productResultRecalculationButtonClicked = 'productResultRecalculationButtonClicked',
  // Integrate your own insights
  tailoringImpactDisabled = 'tailoringImpactDisabled',
  tailoringRiskopportunityDisabled = 'tailoringRiskopportunityDisabled',
  tailoringImpactAdded = 'tailoringImpactAdded',
  tailoringRiskopportunityAdded = 'tailoringRiskopportunityAdded',
  tailoringImpactCopyedAsNew = 'tailoringImpactCopyedAsNew',
  tailoringRiskopportunityCopyedAsNew = 'tailoringRiskopportunityCopyedAsNew',
  tailoringImpactMaterialityThreshold = 'tailoringImpactMaterialityThreshold',
  tailoringFinancialMaterialityThreshold = 'tailoringFinancialMaterialityThreshold',
  // Share your results
  materialityMatrixImageDownloaded = 'materialityMatrixImageDownloaded',

  // Events for funnel tracking
  plgAccountCreationStarted = 'plgAccountCreationStarted',
  plgAccountCreationCompleted = 'plgAccountCreationCompleted',
  plgJoinedOrganisation = 'plgJoinedOrganisation',
  plgConfirmCompanyViewed = 'plgConfirmCompanyViewed',
  plgCompanyConfirmedProductFetchStarted = 'plgCompanyConfirmedProductFetchStarted',
  plgProductsFetchedSuccessfully = 'plgProductsFetchedSuccessfully',
  plgProductModificationOnboardingViewed = 'plgProductModificationOnboardingViewed',
  plgProductModificationOnboardingProductDeleted = 'plgProductModificationOnboardingProductDeleted',
  plgProductModificationOnboardingAllProductsDeleted = 'plgProductModificationOnboardingAllProductsDeleted',
  plgProductModificationOnboardingProductAdded = 'plgProductModificationOnboardingProductAdded',
  plgProductModificationOnboardingProductEdited = 'plgProductModificationOnboardingProductEdited',
  plgProductModificationOnboardingProductSetConfirmed = 'plgProductModificationOnboardingProductSetConfirmed',
  plgAssessmentTakesLongEmailSent = 'plgAssessmentTakesLongEmailSent',
  plgAssessmentCompletedSuccessfully = 'plgAssessmentCompletedSuccessfully',
  plgHomeDashboardAccess = 'plgHomeDashboardAccess',
  plgHomeVideoStarted = 'plgHomeVideoStarted',
  plgHomeVideoCompleted = 'plgHomeVideoCompleted',
  plgHomeViewResultsClicked = 'plgHomeViewResultsClicked',
  plgHomeDocumentationTileClicked = 'plgHomeDocumentationTileClicked',
  plgHomeNewsTileClicked = 'plgHomeNewsTileClicked',
  plgMaterialityMatrixViewed = 'plgMaterialityMatrixViewed',
  plgMaterialityMatrixImageDownloaded = 'plgMaterialityMatrixImageDownloaded',
  plgMaterialTopicsViewed = 'plgMaterialTopicsViewed',
  plgSmpViewed = 'plgSmpViewed',
  plgSmpImpactCardViewed = 'plgSmpImpactCardViewed',
  plgSmpImpactCardImpactCalculationTooltipViewed = 'plgSmpImpactCardImpactCalculationTooltipViewed',
  plgSmpImpactCardProductCalculationTooltipViewed = 'plgSmpImpactCardProductCalculationTooltipViewed',
  plgSmpFinancialMaterialityTabClicked = 'plgSmpFinancialMaterialityTabClicked',
  plgSmpRiskOpportunityCardViewed = 'plgSmpRiskOpportunityCardViewed',
  plgQuantitativeOverviewViewed = 'plgQuantitativeOverviewViewed',
  plgTailoringAccessAddIro = 'plgTailoringAccessAddIro',
  plgTailoringCommitAddIro = 'plgTailoringCommitAddIro',
  plgTailoringCommitDisableIro = 'plgTailoringCommitDisableIro',
  plgTailoringCommitEditIro = 'plgTailoringCommitEditIro',
  plgTailoringOwnInsightsAccessed = 'plgTailoringOwnInsightsAccessed',
  plgTailoringAdjustedImpactThresholdCommit = 'plgTailoringAdjustedImpactThresholdCommit',
  plgTailoringAdjustedFinancialThresholdCommit = 'plgTailoringAdjustedFinancialThresholdCommit',
  plgTailoringChangeLogViewed = 'plgTailoringChangeLogViewed',
  plgTailoringDeleteAllChangesCommit = 'plgTailoringDeleteAllChangesCommit',
  plgProductModificationDashboardViewed = 'plgProductModificationDashboardViewed',
  plgProductModificationDashboardProductDeleted = 'plgProductModificationDashboardProductDeleted',
  plgProductModificationDashboardAllProductsDeleted = 'plgProductModificationDashboardAllProductsDeleted',
  plgProductModificationDashboardProductAdded = 'plgProductModificationDashboardProductAdded',
  plgProductModificationDashboardProductEdited = 'plgProductModificationDashboardProductEdited',
  plgProductModificationDashboardAbandonedWithoutRecompute = 'plgProductModificationDashboardAbandonedWithoutRecompute',
  plgProductModificationDashboardRecomputationStarted = 'plgProductModificationDashboardRecomputationStarted',
  plgProductModificationDashboardRecomputationCompletedSuccessfully = 'plgProductModificationDashboardRecomputationCompletedSuccessfully',
  plgProductModificationDashboardRecomputationFailed = 'plgProductModificationDashboardRecomputationFailed',
  plgSideNavigationFinancialEffectsExpanded = 'plgSideNavigationFinancialEffectsExpanded',
  plgSideNavigationGeographyTooltipViewed = 'plgSideNavigationGeographyTooltipViewed',
  plgSideNavigationDisclosuresTooltipViewed = 'plgSideNavigationDisclosuresTooltipViewed',
  plgSideNavigationFinancialStatementsTooltipViewed = 'plgSideNavigationFinancialStatementsTooltipViewed',
}

export type AppcuesEventPropertiesById = {
  [AppcuesEventId.hasWatchedDmaFreemiumWelcomeVideo]: never;
  [AppcuesEventId.pageViewPlgMaterialityResults]: never;
  [AppcuesEventId.pageViewPlgDmaImpactSidebar]: {
    matterId: SustainabilityMatterId;
  };
  [AppcuesEventId.pageViewPlgDmaFinancialSidebar]: {
    matterId: SustainabilityMatterId;
  };
  [AppcuesEventId.pageViewPlgDmaImpactMaterialitySmp]: {
    matterId: SustainabilityMatterId;
  };
  [AppcuesEventId.pageViewPlgDmaFinancialMaterialitySmp]: {
    matterId: SustainabilityMatterId;
  };
  [AppcuesEventId.productListPageViewed]: never;
  [AppcuesEventId.productIndividualProductSettingDropDownOpened]: ProductEditFormInteraction;
  [AppcuesEventId.productEditTitleFormInteraction]: ProductEditFormInteraction;
  [AppcuesEventId.productEditDescriptionFormInteraction]: ProductEditFormInteraction;
  [AppcuesEventId.productEditRevenueShareFormInteraction]: ProductEditFormInteraction;
  [AppcuesEventId.productEditProductSaveClicked]: ProductEditFormInteraction & {
    savedValues: Pick<PiuProductState, 'title' | 'description' | 'revenueShare'>;
  };
  [AppcuesEventId.productResultRecalculationButtonClicked]: never;
  [AppcuesEventId.tailoringImpactDisabled]: never;
  [AppcuesEventId.tailoringRiskopportunityDisabled]: never;
  [AppcuesEventId.tailoringImpactAdded]: never;
  [AppcuesEventId.tailoringRiskopportunityAdded]: never;
  [AppcuesEventId.tailoringImpactCopyedAsNew]: never;
  [AppcuesEventId.tailoringRiskopportunityCopyedAsNew]: never;
  [AppcuesEventId.tailoringImpactMaterialityThreshold]: never;
  [AppcuesEventId.tailoringFinancialMaterialityThreshold]: never;
  [AppcuesEventId.materialityMatrixImageDownloaded]: never;

  [AppcuesEventId.plgAccountCreationStarted]: never;
  [AppcuesEventId.plgAccountCreationCompleted]: never;
  [AppcuesEventId.plgJoinedOrganisation]: never;
  [AppcuesEventId.plgConfirmCompanyViewed]: never;
  [AppcuesEventId.plgCompanyConfirmedProductFetchStarted]: never;
  [AppcuesEventId.plgProductsFetchedSuccessfully]: never;
  [AppcuesEventId.plgProductModificationOnboardingViewed]: never;
  [AppcuesEventId.plgProductModificationOnboardingProductDeleted]: never;
  [AppcuesEventId.plgProductModificationOnboardingAllProductsDeleted]: never;
  [AppcuesEventId.plgProductModificationOnboardingProductAdded]: never;
  [AppcuesEventId.plgProductModificationOnboardingProductEdited]: never;
  [AppcuesEventId.plgProductModificationOnboardingProductSetConfirmed]: never;
  [AppcuesEventId.plgAssessmentTakesLongEmailSent]: never;
  [AppcuesEventId.plgAssessmentCompletedSuccessfully]: never;
  [AppcuesEventId.plgHomeDashboardAccess]: never;
  [AppcuesEventId.plgHomeVideoStarted]: never;
  [AppcuesEventId.plgHomeVideoCompleted]: never;
  [AppcuesEventId.plgHomeViewResultsClicked]: never;
  [AppcuesEventId.plgHomeDocumentationTileClicked]: never;
  [AppcuesEventId.plgHomeNewsTileClicked]: never;
  [AppcuesEventId.plgMaterialityMatrixViewed]: never;
  [AppcuesEventId.plgMaterialityMatrixImageDownloaded]: never;
  [AppcuesEventId.plgMaterialTopicsViewed]: never;
  [AppcuesEventId.plgSmpViewed]: never;
  [AppcuesEventId.plgSmpImpactCardViewed]: never;
  [AppcuesEventId.plgSmpImpactCardImpactCalculationTooltipViewed]: never;
  [AppcuesEventId.plgSmpImpactCardProductCalculationTooltipViewed]: never;
  [AppcuesEventId.plgSmpFinancialMaterialityTabClicked]: never;
  [AppcuesEventId.plgSmpRiskOpportunityCardViewed]: never;
  [AppcuesEventId.plgQuantitativeOverviewViewed]: never;
  [AppcuesEventId.plgTailoringAccessAddIro]: never;
  [AppcuesEventId.plgTailoringCommitAddIro]: never;
  [AppcuesEventId.plgTailoringCommitDisableIro]: never;
  [AppcuesEventId.plgTailoringCommitEditIro]: never;
  [AppcuesEventId.plgTailoringOwnInsightsAccessed]: never;
  [AppcuesEventId.plgTailoringAdjustedImpactThresholdCommit]: never;
  [AppcuesEventId.plgTailoringAdjustedFinancialThresholdCommit]: never;
  [AppcuesEventId.plgTailoringChangeLogViewed]: never;
  [AppcuesEventId.plgTailoringDeleteAllChangesCommit]: never;
  [AppcuesEventId.plgProductModificationDashboardViewed]: never;
  [AppcuesEventId.plgProductModificationDashboardProductDeleted]: never;
  [AppcuesEventId.plgProductModificationDashboardAllProductsDeleted]: never;
  [AppcuesEventId.plgProductModificationDashboardProductAdded]: never;
  [AppcuesEventId.plgProductModificationDashboardProductEdited]: never;
  [AppcuesEventId.plgProductModificationDashboardAbandonedWithoutRecompute]: never;
  [AppcuesEventId.plgProductModificationDashboardRecomputationStarted]: never;
  [AppcuesEventId.plgProductModificationDashboardRecomputationCompletedSuccessfully]: never;
  [AppcuesEventId.plgProductModificationDashboardRecomputationFailed]: never;
  [AppcuesEventId.plgSideNavigationFinancialEffectsExpanded]: never;
  [AppcuesEventId.plgSideNavigationGeographyTooltipViewed]: never;
  [AppcuesEventId.plgSideNavigationDisclosuresTooltipViewed]: never;
  [AppcuesEventId.plgSideNavigationFinancialStatementsTooltipViewed]: never;
};

type ProductEditFormInteraction = {
  productId: string;
  productTitle: string;
};
